var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import * as pc from "playcanvas";
import { TooltipPortal } from "@animech/ui";
import { useTranslation } from "react-i18next";
import { Unit } from "utility/number";
import { useAssetLoader } from "hooks/playcanvas/asset-loader";
import { useSystemPerformanceValidityMessage } from "hooks/messages";
import { MessagesPopover, MessageLevel, } from "ui/components/elements/messages-popover";
import { StyledCanvasOverlay } from "ui/components/panels/canvas/overlay-elements/canvas-overlay";
import { CameraViewId } from "3d/types";
import { NodeComponentType, TubeType, } from "store/types";
import { useAppDispatch, useAppSelector, selectors, actions } from "store";
import { getCentralizedVacuumPump, defaultCentralPumpModel, objectShapes, placeholderIconSize, } from "3d/constants/common";
import { createIconBillboard } from "3d/icon-billboard";
import { hoveredPlaceholderIconColors, placeholderIconColors, vacuumPumpIconDataUrl, } from "ui/components/elements/icons/vacuum-pump";
import { useNumberConversion } from "hooks/number-conversion";
import { CustomUITooltip } from "ui/components/elements/custom-ui-tooltip";
import { PsEsTooltip } from "ui/components/panels/canvas/overlay-elements/ps-es-tooltip";
import { applyMaterialTransforms, getMaterialAssets, } from "3d/material-effects";
var PUMP_ENTITY_NAME = "Pump";
var PUMP_PLACEHOLDER_ENTITY_NAME = "PumpPlaceholder";
/* Raster size of the placeholder icon texture; the icon is a 43-unit SVG. */
var PUMP_PLACEHOLDER_ICON_PX = 256;
var TOOLTIP_MARGIN = 10;
export var CentralizedVacuumPump = function (_a) {
    var app = _a.app, nodeEntity = _a.nodeEntity, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets, node = _a.node, isMoving = _a.isMoving, position = _a.position;
    var isCentralizedSystem = useAppSelector(selectors.selectIsCentralizedSystem);
    var isSystemPerformanceLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var hasReachedPartSecured = useAppSelector(selectors.selectHasReachedPartSecured);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var showPsEsIndicators = useAppSelector(selectors.selectShowPsEsIndicators);
    var selectedIds = useAppSelector(selectors.selectSelectedIds);
    var gripPositionY = objectShapes[useAppSelector(selectors.selectObjectShape)].gripPositionY;
    var convertNumber = useNumberConversion();
    var dispatch = useAppDispatch();
    var loadAssets = useAssetLoader(app).loadAssets;
    var t = useTranslation().t;
    var vacuumPump = node.component;
    var _b = __read(useState(null), 2), incomingTooltip = _b[0], setIncomingTooltip = _b[1];
    var _c = __read(useState(null), 2), outgoingTooltip = _c[0], setOutgoingTooltip = _c[1];
    var selectedPump = useAppSelector(selectors.selectSelectedCentralPump(node.id));
    var hasSystemData = useAppSelector(selectors.selectSystemDataDefineParameters) !== undefined;
    /* No pump picked yet: the node is a placeholder (see
     * `selectCentralPumpNodesWithoutPump`) and gets the grey "Add pump" icon
     * instead of a pump model. A picked pump whose cad name has no model of its own still
     * falls back to the default model.
     *
     * Only decided once the Tacton data is in: before that every pump reads as
     * unpicked, and the placeholder clears the node's attachments — which
     * deletes the tubes connected to them (`setNodeAttachments`). A saved system
     * would lose its tubes on every reload. */
    var isPlaceholder = hasSystemData && selectedPump === undefined;
    var selectedPumpModel = getCentralizedVacuumPump(selectedPump) || defaultCentralPumpModel;
    var selectedNode = useAppSelector(selectors.selectSelectedNode);
    var selectedNodeIsRegulator = (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.type) === NodeComponentType.FEED_PRESSURE_POINT;
    var performanceData = useAppSelector(selectors.selectVacuumPumpPerformance(node.id));
    var systemPerformanceMessage = useSystemPerformanceValidityMessage();
    useEffect(function () {
        var pumpEntity = new pc.Entity(PUMP_ENTITY_NAME);
        pumpEntity.addComponent("model", { type: "asset" });
        nodeEntity.addChild(pumpEntity);
        return function () {
            pumpEntity.destroy();
        };
    }, [nodeEntity]);
    /* Placeholder: the toolbar's "Add pump" icon in grey, on a camera-facing
     * square (the customer wanted the familiar icon, not a stand-in 3D model),
     * lighting up in dark grey on hover.
     * The pump entity is disabled meanwhile so a model left from a previously
     * picked pump does not show through, and the node has no tube attachments
     * until there is a pump to attach them to. */
    useEffect(function () {
        if (!isPlaceholder)
            return;
        var pumpEntity = nodeEntity.findByName(PUMP_ENTITY_NAME);
        if (pumpEntity)
            pumpEntity.enabled = false;
        var placeholder = createIconBillboard(app, {
            name: PUMP_PLACEHOLDER_ENTITY_NAME,
            imageUrl: vacuumPumpIconDataUrl(PUMP_PLACEHOLDER_ICON_PX, placeholderIconColors),
            emphasizedImageUrl: vacuumPumpIconDataUrl(PUMP_PLACEHOLDER_ICON_PX, hoveredPlaceholderIconColors),
            size: placeholderIconSize,
            sizeAsIfAtHeight: gripPositionY,
        });
        // Centred at half the icon's height so it sits on the node origin like the pump models.
        placeholder.entity.setLocalPosition(0, placeholderIconSize / 2, 0);
        nodeEntity.addChild(placeholder.entity);
        dispatch(actions.setNodeAttachments({
            nodeId: vacuumPump.nodeId,
            placeholders: [],
        }));
        return function () {
            placeholder.destroy();
            if (pumpEntity)
                pumpEntity.enabled = true;
        };
    }, [
        isPlaceholder,
        app,
        nodeEntity,
        dispatch,
        vacuumPump.nodeId,
        gripPositionY,
    ]);
    useEffect(function () {
        if (isPlaceholder)
            return;
        var pumpEntity = nodeEntity.findByName(PUMP_ENTITY_NAME);
        if (!pumpEntity)
            return;
        var pumpAsset = modelAssets[selectedPumpModel.modelName];
        var assetMaterialTransforms = getMaterialAssets(materialAssets, selectedPumpModel.materialTransforms);
        var assets = [pumpAsset];
        if (assetMaterialTransforms) {
            assets.push.apply(assets, __spreadArray([], __read(Object.values(assetMaterialTransforms)), false));
        }
        var updatePump = function () {
            if (pumpEntity.model && pumpAsset) {
                // we need to ensure that models are reloaded when different pumps use the same asset, the reason is that
                // the applyMaterialTransforms-method expects the raw asset since we use name lookup to identify material transforms
                pumpEntity.removeComponent("model");
                pumpEntity.addComponent("model", { type: "asset" });
                pumpEntity.model.asset = pumpAsset;
                assets.push(pumpAsset);
                if (assetMaterialTransforms) {
                    applyMaterialTransforms(pumpEntity, assetMaterialTransforms);
                }
            }
            dispatch(actions.setNodeAttachments({
                nodeId: vacuumPump.nodeId,
                placeholders: [
                    {
                        name: selectedPumpModel.placeholderFront,
                        tubeType: TubeType.VACUUM,
                    },
                    {
                        name: selectedPumpModel.placeholderBack,
                        tubeType: TubeType.COMPRESSED_AIR,
                    },
                ],
            }));
        };
        if (pumpAsset) {
            loadAssets(assets, updatePump);
        }
    }, [
        isPlaceholder,
        selectedPumpModel,
        nodeEntity,
        modelAssets,
        loadAssets,
        dispatch,
        vacuumPump.nodeId,
        materialAssets,
    ]);
    useEffect(function () {
        if (isCentralizedSystem) {
            nodeEntity.enabled = true;
        }
        else {
            nodeEntity.enabled = false;
        }
    }, [isCentralizedSystem, nodeEntity]);
    var cameraViewId = useAppSelector(selectors.selectCameraViewId);
    // Calculate position and determine if tooltips are visible for in/out air-pressure.
    useEffect(function () {
        // If regulator is not selected or zoomed in we dont render any tooltips.
        if (!nodeEntity ||
            !selectedNodeIsRegulator ||
            isMoving ||
            !performanceData ||
            cameraViewId === CameraViewId.NODE_VIEW ||
            isSystemPerformanceLoading) {
            setIncomingTooltip(null);
            setOutgoingTooltip(null);
            return;
        }
        var cameraEntity = app.root.findByName("Camera");
        var camera = cameraEntity === null || cameraEntity === void 0 ? void 0 : cameraEntity.camera;
        if (!camera || !nodeEntity)
            return;
        var incomingPressure = performanceData.compressedAirPressure;
        var outgoingPressure = performanceData.freeRunningVacuumLevel;
        var getAttachmentPointScreenPosition = function (placeholderName) {
            var attachmentPointEntity = nodeEntity.findByName(placeholderName);
            return (attachmentPointEntity &&
                camera.worldToScreen(attachmentPointEntity.getPosition()));
        };
        var incomingTooltipPosition = getAttachmentPointScreenPosition(selectedPumpModel.placeholderBack);
        if (incomingTooltipPosition) {
            setIncomingTooltip(function () { return ({
                x: incomingTooltipPosition.x,
                y: incomingTooltipPosition.y - TOOLTIP_MARGIN,
                text: "".concat(t("canvas.compressed_air_pressure_pump"), ": ").concat(convertNumber(incomingPressure, Unit.MPA).formatted, "\n\t\t\t "),
                placement: "top",
            }); });
        }
        var outgoingTooltipPosition = getAttachmentPointScreenPosition(selectedPumpModel.placeholderFront);
        if (outgoingTooltipPosition) {
            setOutgoingTooltip(function () { return ({
                x: outgoingTooltipPosition.x,
                y: outgoingTooltipPosition.y + TOOLTIP_MARGIN,
                text: "".concat(t("canvas.free_running_vacuum_pump"), ": ").concat(convertNumber(outgoingPressure, Unit.KPA).formatted, "\n\t\t\t\t "),
                placement: "bottom",
            }); });
        }
    }, [
        nodeEntity,
        app,
        isMoving,
        selectedNodeIsRegulator,
        node.id,
        t,
        performanceData,
        cameraViewId,
        isSystemPerformanceLoading,
        selectedPumpModel,
        convertNumber,
    ]);
    var tooltips = [incomingTooltip, outgoingTooltip].filter(Boolean);
    if (selectedNodeIsRegulator) {
        return (_jsx(_Fragment, { children: tooltips.map(function (tooltip) {
                return (tooltip.x &&
                    tooltip.y && (_jsx(_Fragment, { children: _jsx(TooltipPortal, { placement: tooltip.placement, 
                        /* We need to access the styled component directly when used as an anchor element
                         * Otherwise the position cannot be calculated properly for the TooltipPortal */
                        anchorElement: _jsx(StyledCanvasOverlay, { x: tooltip.x, y: tooltip.y }), open: true, children: _jsx(CustomUITooltip, { children: _jsxs("div", { style: {
                                    display: "flex",
                                    alignItems: "center",
                                    pointerEvents: "all",
                                }, children: [tooltip.text, systemPerformanceMessage && (_jsx(MessagesPopover, { messages: [systemPerformanceMessage], level: MessageLevel.WARNING, toggleEvent: "hover", size: "small" }))] }) }) }, tooltip.text) })));
            }) }));
    }
    var showPartSecured = showPsEsIndicators &&
        isCentralizedSystem &&
        hasReachedPartSecured &&
        isPerformanceDataValid &&
        selectedIds.length === 0 &&
        !isMoving;
    return showPartSecured && position ? (_jsx(PsEsTooltip, { anchorX: position.x, anchorY: position.y })) : null;
};
